/**
 * Arbeitsliste in Mission Control (PV-052, A9): „Mein Stapel", „In deinem
 * Verantwortungsbereich" (Code: Aufsicht; nur mit Aufsichtsrolle, nach zuständiger Person
 * gruppiert, Kopf „Rolle – Name", aufklappbar) und „Erledigt" seit dem
 * letzten Prüflauf (eingeklappt, Zähler, Aktion vs. automatisch).
 */
import type { BefundAufsichtGruppe, BefundErledigtListe } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { Badge } from '../badge.js';
import { SectionHead } from '../section-head.js';
import {
  BEFUNDE_QUERY_KEY,
  STUFE_TON,
  useBefundLocale,
  zurueckgestelltBisAktiv,
} from './befund-basis.js';
import { BefundZeile } from './befund-zeile.js';

/**
 * Anzahl, die Mission Control in der Begrüßung nennt: aktive Befunde in „Mein Stapel“ plus die
 * aktiven im Verantwortungsbereich (zurückgestellte zählen nicht). Nutzt dieselben Query-Keys wie
 * die Arbeitsliste, lädt also nichts doppelt. `null`, solange nichts geladen ist.
 */
export function useArbeitslisteAnzahl(an: boolean): number | null {
  const locale = useBefundLocale();
  const stapel = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'stapel', locale],
    queryFn: () => api.listBefundeStapel(locale),
    enabled: an,
  });
  const aufsicht = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'aufsicht', locale],
    queryFn: () => api.listBefundeAufsicht(locale),
    enabled: an,
  });
  if (!an || !stapel.data) return null;
  const imBereich = (aufsicht.data?.gruppen ?? []).reduce(
    (summe, g) => summe + g.befunde.filter((b) => zurueckgestelltBisAktiv(b) === null).length,
    0,
  );
  return stapel.data.items.length + imBereich;
}

export function Arbeitsliste() {
  const { t } = useTranslation();
  const locale = useBefundLocale();
  const stapel = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'stapel', locale],
    queryFn: () => api.listBefundeStapel(locale),
  });
  const aufsicht = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'aufsicht', locale],
    queryFn: () => api.listBefundeAufsicht(locale),
  });
  const erledigt = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'erledigt', locale],
    queryFn: () => api.listBefundeErledigt(locale),
  });
  const items = stapel.data?.items ?? [];

  return (
    <div className="space-y-10">
      <section>
        <SectionHead
          label={t('v2.befund.stapel_titel', 'Mein Stapel')}
          meta={t('v2.befund.stapel_meta', '{{count}} offen', { count: items.length })}
        />
        {stapel.isError ? (
          <Hinweis fehler>
            {t(
              'v2.befund.laden_fehler',
              'Befunde konnten nicht geladen werden. Bitte Seite neu laden.',
            )}
          </Hinweis>
        ) : stapel.isLoading ? (
          <Hinweis>{t('v2.befund.laedt', 'Lädt…')}</Hinweis>
        ) : items.length === 0 ? (
          <Hinweis>{t('v2.befund.stapel_leer', 'Nichts zu tun — dein Stapel ist leer.')}</Hinweis>
        ) : (
          <Liste>
            {items.map((b) => (
              <BefundZeile key={b.befund_id} befund={b} />
            ))}
          </Liste>
        )}
      </section>

      {aufsicht.isError ? (
        <Hinweis fehler>
          {t(
            'v2.befund.laden_fehler',
            'Befunde konnten nicht geladen werden. Bitte Seite neu laden.',
          )}
        </Hinweis>
      ) : (
        aufsicht.data?.hat_aufsicht && <AufsichtAbschnitt gruppen={aufsicht.data.gruppen} />
      )}

      {erledigt.isError ? (
        <Hinweis fehler>
          {t(
            'v2.befund.laden_fehler',
            'Befunde konnten nicht geladen werden. Bitte Seite neu laden.',
          )}
        </Hinweis>
      ) : (
        erledigt.data && <ErledigtAbschnitt daten={erledigt.data} />
      )}
    </div>
  );
}

function AufsichtAbschnitt({ gruppen }: { gruppen: BefundAufsichtGruppe[] }) {
  const { t } = useTranslation();
  return (
    <section>
      <SectionHead
        label={t('v2.befund.aufsicht_titel', 'In deinem Verantwortungsbereich')}
        meta={t('v2.befund.aufsicht_meta', '{{count}} Personen', { count: gruppen.length })}
      />
      {gruppen.length === 0 ? (
        <Hinweis>{t('v2.befund.aufsicht_leer', 'In deinen Projekten ist nichts offen.')}</Hinweis>
      ) : (
        <div className="space-y-2">
          {gruppen.map((g) => (
            <AufsichtGruppe key={g.schluessel} gruppe={g} />
          ))}
        </div>
      )}
    </section>
  );
}

function AufsichtGruppe({ gruppe }: { gruppe: BefundAufsichtGruppe }) {
  const { t, i18n } = useTranslation();
  const [offen, setOffen] = useState(false);
  const name =
    gruppe.zustaendig_name ??
    (gruppe.rolle === 'kfml'
      ? t('v2.befund.rolle_kfml', 'Kaufm. Leitung')
      : t('v2.befund.aufsicht_niemand', 'niemand eingetragen'));
  const aeltester = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
    new Date(gruppe.aeltester),
  );
  return (
    <div
      className="rounded-xl border"
      style={{ borderColor: 'var(--line-soft)', background: 'var(--surface)' }}
    >
      <button
        type="button"
        aria-expanded={offen}
        onClick={() => setOffen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <span className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {t('v2.befund.aufsicht_gruppe', '{{rolle}} – {{name}}', {
            rolle: t(`v2.befund.rolle_kurz_${gruppe.rolle}`, gruppe.rolle),
            name,
          })}
        </span>
        <Badge tone={STUFE_TON[gruppe.hoechste_stufe]}>
          {t(`v2.befund.stufe_${gruppe.hoechste_stufe}`, gruppe.hoechste_stufe)}
        </Badge>
        <span className="text-xs ml-auto" style={{ color: 'var(--ink-3)' }}>
          {t('v2.befund.aufsicht_zeile', '{{anzahl}} offen · ältester seit {{datum}}', {
            anzahl: gruppe.anzahl,
            datum: aeltester,
          })}{' '}
          {offen ? '▴' : '▾'}
        </span>
      </button>
      {offen && (
        <ul className="px-4 pb-2">
          {gruppe.befunde.map((b) => (
            <BefundZeile key={b.befund_id} befund={b} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ErledigtAbschnitt({ daten }: { daten: BefundErledigtListe }) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(false);
  return (
    <section>
      <button
        type="button"
        aria-expanded={offen}
        onClick={() => setOffen((v) => !v)}
        className="text-sm font-medium"
        style={{ color: 'var(--ink-2)' }}
      >
        {t('v2.befund.erledigt_titel', 'Erledigt seit dem letzten Prüflauf ({{count}})', {
          count: daten.items.length,
        })}{' '}
        {offen ? '▴' : '▾'}
      </button>
      {offen &&
        (daten.items.length === 0 ? (
          <Hinweis>
            {t('v2.befund.erledigt_leer', 'Seit dem letzten Prüflauf ist nichts erledigt worden.')}
          </Hinweis>
        ) : (
          <ul className="mt-2 space-y-1">
            {daten.items.map((b) => (
              <li
                key={b.befund_id}
                className="flex items-center gap-2 text-sm"
                style={{ color: 'var(--ink-2)' }}
              >
                <span style={{ fontFamily: 'var(--f-mono)' }}>{b.project_key}</span>
                <span>{b.regel_titel}</span>
                <span className="text-[11px] ml-auto" style={{ color: 'var(--ink-3)' }}>
                  {b.erledigt_weg === 'aktion'
                    ? t('v2.befund.erledigt_aktion', 'durch Aktion')
                    : t('v2.befund.erledigt_automatisch', 'automatisch')}
                </span>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}

function Liste({ children }: { children: ReactNode }) {
  return (
    <ul
      className="rounded-xl border px-4"
      style={{ borderColor: 'var(--line-soft)', background: 'var(--surface)' }}
    >
      {children}
    </ul>
  );
}

function Hinweis({ children, fehler = false }: { children: ReactNode; fehler?: boolean }) {
  return (
    <p className="text-sm px-1" style={{ color: fehler ? 'var(--danger)' : 'var(--ink-3)' }}>
      {children}
    </p>
  );
}
